/**
 * Ball trail, coloured by who has possession (founder ask, 2026-08-27).
 *
 * The ball's path over the last few seconds, drawn as a polyline whose colour
 * changes with the possessing team — so the picture answers "who has had it,
 * and where" in one glance rather than requiring the viewer to track a dot.
 *
 * Positions come from the frame buffer the canvas controller already holds
 * (no fetch). Possession does NOT: there is no possession field on a tracking
 * frame — the tracking parquet carries FrameCount, PlayerPositions and
 * BallPosition and nothing else. It comes from the possession model
 * (analytics/possession_model.parquet, 439 segments for this game) via
 * /game-match-summary/api/possession-model/<code>, fetched once and indexed by
 * frame range.
 *
 * When that model is absent — it is generated by the analytics pipeline and
 * not every game has one — the trail still draws, in neutral grey. A missing
 * model must not remove the ball's path, only its colour.
 */
import type { TrackingFrame } from './trackingLoader'
import { pathSeg } from '../../lib/urls'

/** 10s at 30fps. Long enough to show a move, short enough to stay a line. */
export const TRAIL_FRAMES = 300

export interface PossessionSegment {
  start_frame: number
  end_frame: number
  team_id: number
}

/** Neutral: no possession model, or a gap between segments (a loose ball). */
export const NEUTRAL = '#9aa4b6'

/**
 * Which team held the ball on `frame`, or null.
 *
 * Segments are sorted and non-overlapping, so this is a binary search rather
 * than a scan — it is called once per point per animation frame.
 */
export function teamAtFrame(segments: PossessionSegment[], frame: number): number | null {
  let lo = 0
  let hi = segments.length - 1
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    const s = segments[mid]
    if (s === undefined) break
    if (frame < s.start_frame) hi = mid - 1
    else if (frame > s.end_frame) lo = mid + 1
    else return s.team_id
  }
  return null
}

/** Flatten the API's {home, away} envelope into one frame-ordered list. */
export function flattenSegments(payload: unknown): PossessionSegment[] {
  const p = payload as { segments?: { home?: unknown[]; away?: unknown[] } } | null
  const all = [...(p?.segments?.home ?? []), ...(p?.segments?.away ?? [])] as PossessionSegment[]
  return all
    .filter(s => Number.isFinite(s?.start_frame) && Number.isFinite(s?.end_frame))
    .sort((a, b) => a.start_frame - b.start_frame)
}

interface BallTrailOptions {
  getGameCode: () => string | null
  getCanvasState: () => {
    ctx: CanvasRenderingContext2D | null
    frames: TrackingFrame[]
    currentFrameIndex: number
  }
  convertToCanvasCoords: (x: number, y: number) => { x: number; y: number }
  getTeamColors: () => Record<number, string>
}

export class BallTrailManager {
  enabled = false
  config = { trailFrames: TRAIL_FRAMES, width: 3.5 }

  private opts: BallTrailOptions
  private segments: PossessionSegment[] = []
  private loaded = false
  private loading: Promise<void> | null = null

  constructor(opts: BallTrailOptions) {
    this.opts = opts
  }

  /** Fetch the possession model once. Safe to call repeatedly. */
  async ensureLoaded(): Promise<void> {
    if (this.loaded) return
    if (this.loading) return this.loading
    const code = this.opts.getGameCode()
    if (!code) return
    this.loading = (async () => {
      try {
        const r = await fetch(
          `/api/v1/game-match-summary/possession-model/${pathSeg(code)}`,
          { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        if (r.ok) this.segments = flattenSegments(await r.json())
      } catch {
        // Colour is an enhancement; the trail draws grey without it.
      } finally {
        this.loaded = true
        this.loading = null
      }
    })()
    return this.loading
  }

  draw(): void {
    if (!this.enabled) return
    const { ctx, frames, currentFrameIndex } = this.opts.getCanvasState()
    if (!ctx || !frames.length) return

    const end = Math.min(currentFrameIndex, frames.length - 1)
    const start = Math.max(0, end - this.config.trailFrames + 1)
    const colors = this.opts.getTeamColors()

    ctx.save()
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'

    let prev: { x: number; y: number } | null = null
    for (let i = start; i <= end; i++) {
      const b = frames[i]?.ball
      if (!b || !Number.isFinite(b.x) || !Number.isFinite(b.y)) {
        // A gap in ball detection breaks the line rather than drawing a chord
        // straight through it — the ball did not travel that path.
        prev = null
        continue
      }
      const pt = this.opts.convertToCanvasCoords(b.x, b.y)
      const frameAtI = frames[i]
      if (prev && frameAtI) {
        const team = teamAtFrame(this.segments, frameAtI.frame)
        // Older points are fainter, so the head of the trail reads as "now".
        ctx.globalAlpha = 0.15 + 0.75 * ((i - start) / Math.max(1, end - start))
        ctx.strokeStyle = team == null ? NEUTRAL : (colors[team] || NEUTRAL)
        ctx.lineWidth = this.config.width
        ctx.beginPath()
        ctx.moveTo(prev.x, prev.y)
        ctx.lineTo(pt.x, pt.y)
        ctx.stroke()
      }
      prev = pt
    }

    // The ball's current position, so the head of the trail is unambiguous.
    if (prev) {
      ctx.globalAlpha = 1
      ctx.fillStyle = '#fff'
      ctx.strokeStyle = '#101318'
      ctx.lineWidth = 1.5
      ctx.beginPath()
      ctx.arc(prev.x, prev.y, 4.5, 0, Math.PI * 2)
      ctx.fill()
      ctx.stroke()
    }
    ctx.restore()
  }
}
